import React, { useEffect, useMemo, useState } from "react";
import API from "../../api";
import "../css/ListTickets.css";
import TicketDetailDrawer from "./TicketDetailDrawer";

function safeJsonParse(s) {
  try { return JSON.parse(s); } catch { return null; }
}

function normalizeRole(role) {
  return String(role || "").trim().toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");
}

function roleForApi(role) {
  const r = normalizeRole(role);
  if (r === "subadmin") return "sub-admin";
  return r;
}

function getUserFromToken() {
  const tok = window.localStorage.getItem("token") || window.sessionStorage.getItem("token");
  if (!tok) return {};
  const parts = tok.split(".");
  if (parts.length < 2) return {};
  try {
    const json = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return json?.user || json || {};
  } catch { return {}; }
}

function getPersistedAuth() {
  for (const key of ["persist:root", "persistroot"]) {
    const raw = window.localStorage.getItem(key);
    if (!raw) continue;
    const root = safeJsonParse(raw);
    if (!root) continue;
    const authObj = typeof root?.auth === "string" ? safeJsonParse(root.auth) : root?.auth;
    if (authObj) return authObj;
  }
  return null;
}

function getCurrentAuth() {
  const persisted = getPersistedAuth();
  if (persisted) {
    const pUser = persisted?.user || persisted;
    const username = pUser?.username || persisted?.username || "";
    const role = pUser?.role || persisted?.role || "";
    return { username: String(username || ""), role: String(role || "") };
  }
  const tUser = getUserFromToken();
  const tUsername = tUser?.username || tUser?.preferred_username || "";
  const tRole = tUser?.role || "";
  if (tUsername || tRole) return { username: String(tUsername || ""), role: String(tRole || "") };
  const rawUser = window.localStorage.getItem("user");
  if (rawUser) {
    const uObj = safeJsonParse(rawUser);
    if (uObj) {
      const uUsername = uObj?.username || "";
      const uRole = uObj?.role || "";
      if (uUsername || uRole) return { username: String(uUsername || ""), role: String(uRole || "") };
    }
  }
  const lsRole = window.localStorage.getItem("role") || "";
  return { username: "", role: String(lsRole) };
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  try {
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  } catch { return String(value); }
}

// ── EST date helpers (same as HotLead) ───────────────────────────────────────
const EST_TZ = "America/New_York";

function getESTParts(date) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: EST_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return { y: Number(get("year")), m: Number(get("month")), d: Number(get("day")) };
}

function startOfDay(dt = new Date()) {
  const { y, m, d } = getESTParts(dt);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

function endOfDay(dt = new Date()) {
  const { y, m, d } = getESTParts(dt);
  return new Date(y, m - 1, d, 23, 59, 59, 999);
}

function startOfESTFromInput(yyyy_mm_dd) {
  const [y, m, d] = yyyy_mm_dd.split("-").map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

function endOfESTFromInput(yyyy_mm_dd) {
  const [y, m, d] = yyyy_mm_dd.split("-").map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999);
}

function getCallDateTime(callDate, callTime) {
  if (!callDate) return null;
  const d = new Date(callDate);
  if (Number.isNaN(d.getTime())) return null;
  if (callTime) {
    const [hh = "0", mm = "0", ss = "0"] = String(callTime).split(":");
    d.setHours(Number(hh), Number(mm), Number(ss));
  } else {
    d.setHours(0, 0, 0, 0);
  }
  return d;
}

function formatCallDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch { return null; }
}

function formatCallTime12h(calltime) {
  if (!calltime) return null;
  const m = String(calltime).trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  let displayHour = hh % 12;
  if (displayHour === 0) displayHour = 12;
  const suffix = hh >= 12 ? "PM" : hh >= 10 ? "AM" : "PM";
  return `${displayHour}:${String(mm).padStart(2, "0")} ${suffix}`;
}
// ─────────────────────────────────────────────────────────────────────────────

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "open") return "tk-badge tk-open";
  if (s === "close") return "tk-badge tk-close";
  return "tk-badge";
}

export default function ListTickets() {
  const [auth, setAuth] = useState({ username: "", role: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // ── dropdown / text filters ──
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [category, setCategory] = useState("");

  // ── date mode filter (frontend-only) ──
  const [dateMode, setDateMode] = useState("All"); // 'All' | 'Today' | 'Past' | 'Upcoming' | 'Range'
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  // ── detail drawer ──
  const [openTicketId, setOpenTicketId] = useState(null);

  const roleNorm = useMemo(() => normalizeRole(auth.role), [auth.role]);
  const isAgent = roleNorm === "agent";

  const createdByOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.created_by).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const categoryOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.category).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const rangeLabel = useMemo(() => {
    if (dateMode !== "Range") return "";
    if (!rangeStart && !rangeEnd) return "Date Range";
    const fmt = (s) => new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    if (rangeStart && rangeEnd) return `${fmt(rangeStart)} – ${fmt(rangeEnd)}`;
    if (rangeStart) return `${fmt(rangeStart)} →`;
    return `← ${fmt(rangeEnd)}`;
  }, [dateMode, rangeStart, rangeEnd]);

  const filteredRows = useMemo(() => {
    const now = new Date();
    const todayStart = startOfDay(now);
    const todayEnd = endOfDay(now);

    return rows.filter((r) => {
      // category (frontend-only)
      if (category && String(r.category || "") !== String(category)) return false;

      // date mode
      if (dateMode !== "All") {
        const dt = getCallDateTime(r.call_date, r.call_time);
        if (!dt) return false;
        if (dateMode === "Today") return dt >= todayStart && dt <= todayEnd;
        if (dateMode === "Past") return dt < todayStart;
        if (dateMode === "Upcoming") return dt > todayEnd;
        if (dateMode === "Range") {
          if (!rangeStart && !rangeEnd) return true;
          const from = rangeStart ? startOfESTFromInput(rangeStart) : new Date(-8640000000000000);
          const to = rangeEnd ? endOfESTFromInput(rangeEnd) : new Date(8640000000000000);
          return dt >= from && dt <= to;
        }
      }
      return true;
    });
  }, [rows, category, dateMode, rangeStart, rangeEnd]);

  async function fetchTickets() {
    try {
      setLoading(true);
      setErr("");
      if (isAgent) { setRows([]); return; }

      const resp = await API.post("/auth/ticket-list", {
        role: roleForApi(auth.role),
        username: auth.username,
        q,
        status,
        created_by: createdBy,
      });
      setRows(Array.isArray(resp?.data?.items) ? resp.data.items : []);
    } catch (e) {
      console.error(e);
      if (e?.response?.status === 403) { setRows([]); setErr(""); return; }
      setErr(e?.response?.data?.error || "Could not load tickets.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const a = getCurrentAuth();
    setAuth({ username: a.username, role: a.role });
  }, []);

  useEffect(() => {
    if (!auth.role) return;
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.role]);

  useEffect(() => {
    if (!auth.role) return;
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, createdBy]);

  if (isAgent) {
    return (
      <div className="tk-wrap">
        <div className="tk-header"><div className="tk-title">Support Tickets</div></div>
        <div className="tk-table">
          <div className="tk-row"><div className="tk-col tk-full tk-error">You are not authorized</div></div>
        </div>
      </div>
    );
  }

  const hasActiveFilters = q || status || createdBy || category || dateMode !== "All";

  return (
    <div className="tk-wrap">
      <div className="tk-header">
        <div className="tk-title">Support Tickets</div>

        {/* ── Date mode chips (same as HotLead) ── */}
        <div className="tk-date-bar">
          <div className="tk-filter-group">
            {["All", "Today", "Past", "Upcoming"].map((mode) => (
              <button
                key={mode}
                type="button"
                className={`tk-filter-chip${dateMode === mode ? " active" : ""}`}
                onClick={() => setDateMode(mode)}
              >
                {mode === "All" ? "Call Date All" : mode}
              </button>
            ))}

            {/* Range picker */}
            <div className={`tk-filter-chip tk-filter-range${dateMode === "Range" ? " active" : ""}`}>
              <button
                type="button"
                className="tk-filter-range-toggle"
                onClick={() => setDateMode("Range")}
              >
                {rangeLabel || "Date Range"}
              </button>
              {dateMode === "Range" && (
                <div className="tk-range-pop">
                  <label>
                    From
                    <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} />
                  </label>
                  <label>
                    To
                    <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} />
                  </label>
                  <div className="tk-range-actions">
                    <button type="button" onClick={() => { setRangeStart(""); setRangeEnd(""); }}>Clear</button>
                    <button type="button" onClick={() => setDateMode("Range")}>Apply</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Existing dropdown / text filters ── */}
        <div className="tk-filters">
          <input
            className="tk-input"
            placeholder="Search (merchant, title, category)…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          <select className="tk-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All status</option>
            <option value="open">Open</option>
            <option value="close">Close</option>
          </select>

          <select className="tk-input" value={createdBy} onChange={(e) => setCreatedBy(e.target.value)}>
            <option value="">All created_by</option>
            {createdByOptions.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>

          <select className="tk-input" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All category</option>
            {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>

          {hasActiveFilters && (
            <button
              type="button"
              className="tk-btn"
              onClick={() => {
                setQ(""); setStatus(""); setCreatedBy(""); setCategory("");
                setDateMode("All"); setRangeStart(""); setRangeEnd("");
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="tk-table">
        <div className="tk-row tk-head">
          <div className="tk-col">ID</div>
          <div className="tk-col">Merchant</div>
          <div className="tk-col">Title</div>
          <div className="tk-col">Call Date & Time</div>
          <div className="tk-col">Category</div>
          <div className="tk-col">Created By</div>
          <div className="tk-col">Status</div>
          <div className="tk-col">Created At</div>
        </div>

        {loading && <div className="tk-row"><div className="tk-col tk-full">Loading…</div></div>}
        {err && <div className="tk-row"><div className="tk-col tk-full tk-error">{err}</div></div>}
        {!loading && !err && filteredRows.length === 0 && (
          <div className="tk-row"><div className="tk-col tk-full">No tickets.</div></div>
        )}

        {!loading && !err && filteredRows.map((r) => (
          <div
            key={r.id}
            className="tk-row tk-click"
            onClick={() => setOpenTicketId(r.id)}
            role="button"
            tabIndex={0}
          >
            <div className="tk-col">{r.id}</div>
            <div className="tk-col">{r.merchant_id || "—"}</div>
            <div className="tk-col">{r.title || "—"}</div>
            <div className="tk-col">
              {r.call_date ? (
                <>
                  <div>{formatCallDate(r.call_date)}</div>
                  <div className="tk-muted">{formatCallTime12h(r.call_time) || "—"}</div>
                </>
              ) : (
                <span className="tk-muted">—</span>
              )}
            </div>
            <div className="tk-col">{r.category || "—"}</div>
            <div className="tk-col">{r.created_by || "—"}</div>
            <div className="tk-col">
              <span className={statusBadgeClass(r.status)}>{r.status}</span>
            </div>
            <div className="tk-col">{formatDateTime(r.created_at)}</div>
          </div>
        ))}
      </div>

      <TicketDetailDrawer
        open={!!openTicketId}
        ticketId={openTicketId}
        onClose={() => setOpenTicketId(null)}
        currentRole={roleForApi(auth.role)}
        currentUsername={auth.username}
        onChanged={() => fetchTickets()}
      />
    </div>
  );
}
